import { cardCoverFrame, type CardCoverSpec } from './cardCovers';
import {
  cardText,
  cardTextWidth,
  cardItemsBounds,
  cardTypeCss,
  cardWatermark,
  fitCardText,
  round2,
  shiftCardItem,
  type KitCardFont,
  type KitCardItem,
  type KitCardLayout,
  type KitCardLayoutInput,
  type KitCardText,
  type KitCardWatermark,
  type KitLinkPreviewCard,
} from './kitCard';
import { storyThemeIdOf } from './storyFrames';
import type { StoryBox } from './storyLayout';
import { groundDots, mixHex, storyAlpha, type StoryColours, type StoryCss } from './storyStyles';
import { WATERMARK_PLACEMENTS, watermarkGap, watermarkLockup } from './watermark';
import type { ReleaseThemeId } from '../releaseThemes';

/**
 * The link preview (og:image) of a SmartLink (design `asset-redesign/m Link preview - new`,
 * `LinkPreviewFrame.dc.html`, owner answers 2026-10-08, the "with title" layout). Three columns in
 * the content box, centred on the height: on the left "New single", the song title and the artist
 * (right-aligned to the cover); the cover in the frame of the theme in the centre square; on the
 * right the status badge ("Out 16 Oct Friday", "Out Now", "Coming Soon"). No URL, no logo. Every
 * size is in px of the 1200 x 630 file. Pure.
 */

export const LINK_PREVIEW_SIZE = { width: 1200, height: 630 } as const;
/** The content box (x 64 to 1136, y 56 to 574; X crops 15 px off the top and the bottom). */
export const LINK_PREVIEW_BOX: StoryBox = { x: 64, y: 56, w: 1072, h: 518 };
/** The centre square that the square crops keep (x 285 to 915). */
export const LINK_PREVIEW_SQUARE: StoryBox = { x: 285, y: 0, w: 630, h: 630 };
/** The X title label: keep it clear of text and cover (x 28 to 340, y 520 to 600). */
export const LINK_PREVIEW_X_LABEL: StoryBox = { x: 28, y: 520, w: 312, h: 80 };

/** The sizes of page m ("Build spec"), before the factors of the theme. */
export const LINK_PREVIEW_SIZES = {
  /** The width of the left and the right column. */
  column: 271,
  columnGap: 40,
  cover: 450,
  /** Retro and Handmade: the record and the mat need room. */
  coverSmall: 410,
  leftGap: 14,
  kicker: 28,
  title: 62,
  /** The title never goes below 40 px before it wraps (then it shrinks until it fits). */
  titleFloorPx: 40,
  artist: 32,
  noirLine: { w: 120, h: 8 },
  badge: { padTop: 22, padX: 30, padBottom: 24, gap: 6, radius: 30 },
  badgeTop: 26,
  badgeValue: 80,
  badgeSub: 26,
} as const;

/**
 * The Free watermark of a link preview (design w0: a stack, L 28, under the badge, from y 440): centred
 * on the badge, at least 440 px down and under the badge, inside the content box and the right column. Pure.
 */
export function linkPreviewWatermark(badge: ReadonlyArray<KitCardItem>, columnX: number): KitCardWatermark {
  const place = WATERMARK_PLACEMENTS.linkPreview;
  const columnRight = LINK_PREVIEW_BOX.x + LINK_PREVIEW_BOX.w;
  const lockup = watermarkLockup(place.form, place.L);
  const bounds = cardItemsBounds(badge);
  const centre = badge.length === 0 ? columnX + LINK_PREVIEW_SIZES.column / 2 : bounds.x + bounds.w / 2;
  const x = Math.min(columnRight - lockup.w, Math.max(columnX, centre - lockup.w / 2));
  const under = badge.length === 0 ? 0 : bounds.y + bounds.h + watermarkGap(place.L);
  const y = Math.min(LINK_PREVIEW_BOX.y + LINK_PREVIEW_BOX.h - lockup.h, Math.max(440, under));
  return cardWatermark({ x, y, form: place.form, L: place.L, maxWidth: columnRight - x, align: 'left' });
}

/** The factors of each theme (`a / b / t` of page m): the title, the badge value and the small texts. */
export const LINK_PREVIEW_FACTORS: Readonly<Record<ReleaseThemeId, { title: number; badge: number; text: number }>> = {
  clean: { title: 0.98, badge: 0.72, text: 1 },
  hype: { title: 1.15, badge: 0.88, text: 1 },
  dreamy: { title: 1.3, badge: 0.9, text: 0.95 },
  dark: { title: 0.74, badge: 0.56, text: 0.9 },
  warm: { title: 0.95, badge: 0.7, text: 1 },
  retro: { title: 0.8, badge: 0.56, text: 0.95 },
  neon: { title: 0.66, badge: 0.5, text: 0.9 },
  handmade: { title: 1.25, badge: 1, text: 1.2 },
};

const LINE = { kicker: 1.1, title: 0.98, artist: 1.1, badgeSmall: 1, badgeValue: 0.95 } as const;

const COVER_SPEC: CardCoverSpec = {
  clean: { radius: 22, shadow: [34, 60, -26] },
  hype: { turn: -4, border: 14, shadow: 16 },
  dreamy: { radius: 56, glow: [30, 80, -20] },
  dark: { pad: 16, outline: 3, grey: 0.35 },
  warm: { radius: 24, shadow: [30, 60, -26] },
  retro: { border: 12, shadow: [22, 40, -22], record: { top: '-9%', right: '-9%', width: '80%', inset: 18 } },
  neon: { pad: 12, border: 6, radius: 30, glow: 28, inset: 18, pictureRadius: 16 },
  handmade: { pad: [14, 14, 48], turn: -4, shadow: [22, 36, -18], tape: [-22, 44] },
};

function coverWidth(id: ReleaseThemeId): number {
  return id === 'retro' || id === 'handmade' ? LINK_PREVIEW_SIZES.coverSmall : LINK_PREVIEW_SIZES.cover;
}

/** The colour of the title: the accent, or the text colour (Noir, Retro, Neon). */
function heroColour(id: ReleaseThemeId, colors: StoryColours): string {
  return id === 'dark' || id === 'retro' || id === 'neon' ? colors.text : colors.accent;
}

function titleCss(id: ReleaseThemeId, colors: StoryColours): StoryCss {
  const color = heroColour(id, colors);
  switch (id) {
    case 'hype':
      return { color, textShadow: `6px 6px 0 ${colors.surface}` };
    case 'retro':
      return { color, textShadow: `5px 5px 0 ${colors.accent}` };
    case 'neon':
      return { color, textShadow: '0 0 12px currentColor, 0 0 30px currentColor' };
    case 'handmade':
      return { color, transform: 'rotate(-3deg)' };
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
      return { color };
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The badge of a theme: the plate colours, the padding and the look (page m, the theme table). */
interface BadgeLook {
  background: string;
  color: string;
  padTop: number;
  padX: number;
  padBottom: number;
  /** A border inside the plate (Retro, Neon), in px. */
  border: number;
  /** The turn of the whole badge, in degrees. */
  turn: number;
  plate: StoryCss;
  /** The look of the small texts ("Out", "Friday"). */
  small: StoryCss;
}

function badgeLook(id: ReleaseThemeId, colors: StoryColours): BadgeLook {
  const { padTop, padX, padBottom, radius } = LINK_PREVIEW_SIZES.badge;
  const base = { padTop, padX, padBottom, border: 0, turn: 0, small: {} as StoryCss };
  const plain = (background: string, color: string): BadgeLook => ({
    ...base,
    background,
    color,
    plate: { background, borderRadius: radius },
  });
  switch (id) {
    case 'clean':
    case 'retro':
    case 'warm':
    case 'dreamy': {
      const look = plain(colors.accent, colors.onAccent);
      if (id === 'dreamy') {
        return { ...look, padTop: 26, padX: 40, padBottom: 28, plate: { ...look.plate, borderRadius: 999 } };
      }
      if (id === 'warm') {
        return { ...look, padTop: 34, plate: { ...look.plate, borderRadius: '999px 999px 24px 24px' } };
      }
      if (id === 'retro') {
        return { ...look, border: 6, plate: { ...look.plate, borderRadius: 14, border: `6px solid ${colors.text}` } };
      }
      return look;
    }
    case 'hype': {
      const look = plain(colors.surface, colors.accent);
      return { ...look, turn: 4, plate: { ...look.plate, borderRadius: 18 } };
    }
    case 'dark': {
      const look = plain(colors.text, colors.ink);
      return {
        ...look,
        plate: { ...look.plate, borderRadius: 0, outline: `3px solid ${colors.text}`, outlineOffset: 8 },
      };
    }
    case 'neon':
      return {
        ...base,
        background: 'transparent',
        color: colors.accent,
        border: 5,
        plate: {
          background: 'transparent',
          borderRadius: radius,
          border: `5px solid ${colors.accent}`,
          boxShadow: `0 0 22px ${colors.accent}, inset 0 0 12px ${storyAlpha(colors.accent, 0.5)}`,
        },
        small: { textShadow: `0 0 10px ${colors.accent}` },
      };
    case 'handmade': {
      const look = plain(colors.text, colors.ink);
      return { ...look, turn: 3, plate: { ...look.plate, borderRadius: 8 } };
    }
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The ground of the link preview of each theme (`LinkPreviewFrame` `.lf-ground`). */
export function linkPreviewGround(themeId: string, colors: StoryColours): string | null {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'clean':
      return `radial-gradient(ellipse 34% 60% at 50% 50%, ${storyAlpha(colors.surface, 0.95)}, transparent 75%)`;
    case 'hype':
      return `radial-gradient(ellipse 36% 60% at 50% 50%, ${storyAlpha(colors.accent, 0.22)}, transparent 70%)`;
    case 'dreamy':
      return [
        'radial-gradient(ellipse 45% 45% at 75% 0%, rgba(255,255,255,.55), transparent 70%)',
        `radial-gradient(ellipse 50% 50% at 25% 100%, ${storyAlpha(colors.accent, 0.25)}, transparent 70%)`,
        `linear-gradient(180deg, ${colors.ink} 0%, ${colors.surface} 100%)`,
      ].join(', ');
    case 'dark':
      return `${groundDots('rgba(255,255,255,.07)', 6, 1)}, radial-gradient(ellipse 30% 60% at 50% 50%, ${colors.surface}, transparent 75%)`;
    case 'warm':
      return `radial-gradient(ellipse 40% 70% at 50% 15%, ${storyAlpha(colors.accent, 0.3)}, transparent 70%), linear-gradient(180deg, ${colors.surface} 0%, ${colors.ink} 70%)`;
    case 'retro':
      return groundDots('rgba(255,255,255,.08)', 6, 1);
    case 'neon':
      return `radial-gradient(ellipse 30% 60% at 50% 50%, ${storyAlpha(colors.accent, 0.2)}, transparent 70%), radial-gradient(ellipse 30% 50% at 15% 40%, ${storyAlpha(colors.text, 0.18)}, transparent 70%)`;
    case 'handmade':
      return `${groundDots('rgba(80,50,30,.05)', 9, 1)}, radial-gradient(ellipse 40% 70% at 50% 45%, rgba(255,255,255,.5), transparent 75%)`;
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

const abs = (style: StoryCss): StoryCss => ({ position: 'absolute', ...style });

/** The torn top edge of the Handmade strip of the link preview (page m). */
const TORN_STRIP_PATH =
  'polygon(0 12%, 6% 0, 13% 10%, 21% 2%, 30% 12%, 39% 3%, 48% 11%, 57% 1%, 66% 10%, 75% 2%, 84% 12%, 93% 3%, 100% 9%, 100% 100%, 0 100%)';

/**
 * The ornaments of the link preview (`.lf-deco`). Hype: stripes top-left and bottom-left, an accent
 * line bottom-right. Retro: three arcs from the bottom-left (they end before x 600). Handmade: the
 * torn strip under the right 44 %.
 */
export function linkPreviewDeco(themeId: string, colors: StoryColours): StoryCss[] {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return [
        abs({
          left: '-6%',
          width: '40%',
          top: '-4%',
          height: '8%',
          background: colors.surface,
          transform: 'rotate(-6deg)',
        }),
        abs({
          left: '-6%',
          width: '36%',
          bottom: '0%',
          height: '5%',
          background: colors.surface,
          transform: 'rotate(-6deg)',
        }),
        abs({
          right: '-6%',
          width: '34%',
          bottom: '7%',
          height: '1.6%',
          background: colors.accent,
          transform: 'rotate(-6deg)',
        }),
      ];
    case 'retro': {
      const arc = (top: string, background: string): StoryCss =>
        abs({ left: '-22%', width: '60%', top, height: '120%', borderRadius: '50%', background });
      return [arc('82%', mixHex(colors.text, colors.ink, 0.7)), arc('85%', colors.surface), arc('88%', colors.accent)];
    }
    case 'handmade':
      return [
        abs({
          right: '-2%',
          width: '44%',
          bottom: '-1%',
          height: '7%',
          background: colors.surface,
          transform: 'rotate(1.5deg)',
          clipPath: TORN_STRIP_PATH,
        }),
      ];
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** Turns an item about a point of the file (the centre of a group), so a group turns as one. */
function turnAbout<T extends KitCardItem>(item: T, degrees: number, cx: number, cy: number): T {
  if (degrees === 0) {
    return item;
  }
  const origin = `${round2(cx - item.box.x)}px ${round2(cy - item.box.y)}px`;
  if (item.kind === 'text' || item.kind === 'shape') {
    return { ...item, css: { ...item.css, transform: `rotate(${degrees}deg)`, transformOrigin: origin } };
  }
  return item;
}

/**
 * The status badge: a plate and three centred texts ("Out", the value, the weekday), left-aligned
 * in the right column and centred on the height. The whole badge turns as one (Hype, Handmade). Pure.
 */
function badgeItems(input: {
  card: KitLinkPreviewCard;
  id: ReleaseThemeId;
  colors: StoryColours;
  display: KitCardFont;
  text: KitCardFont;
  factors: { title: number; badge: number; text: number };
  x: number;
  centreY: number;
}): KitCardItem[] {
  const { card, id, colors, display, text, factors } = input;
  const look = badgeLook(id, colors);
  const smallFont = (weight: number, tracking: number): KitCardFont => ({
    id: text.id,
    style: { ...text.style, weight, tracking, case: 'uppercase' },
  });
  const topFont = smallFont(800, 0.16);
  // The weekday keeps its own case (`.lf-bw` has no text transform).
  const subFont: KitCardFont = { id: text.id, style: { ...text.style, weight: 700, tracking: 0.06, case: 'none' } };
  const inner = LINK_PREVIEW_SIZES.column - 2 * (look.padX + look.border);
  const topFit = fitCardText(card.badge.top, topFont, LINK_PREVIEW_SIZES.badgeTop * factors.text, inner);
  const valueFit = fitCardText(card.badge.value, display, LINK_PREVIEW_SIZES.badgeValue * factors.badge, inner);
  const subFit =
    card.badge.sub === null
      ? null
      : fitCardText(card.badge.sub, subFont, LINK_PREVIEW_SIZES.badgeSub * factors.text, inner);
  const widths = [
    cardTextWidth(topFit.lines[0] ?? '', topFont, topFit.sizePx),
    cardTextWidth(valueFit.lines[0] ?? '', display, valueFit.sizePx),
    subFit === null ? 0 : cardTextWidth(subFit.lines[0] ?? '', subFont, subFit.sizePx),
  ];
  const textW = Math.min(inner, Math.max(...widths));
  const plateW = textW + 2 * (look.padX + look.border);
  const textX = input.x + look.padX + look.border;
  const neonValue = id === 'neon' ? { textShadow: '0 0 12px currentColor, 0 0 30px currentColor' } : {};

  const texts: KitCardText[] = [];
  let y = look.padTop + look.border;
  const push = (item: KitCardText): void => {
    texts.push(item);
    y = item.box.y + item.box.h + LINK_PREVIEW_SIZES.badge.gap;
  };
  push(
    cardText({
      role: 'badgeTop',
      x: textX,
      y,
      lines: topFit.lines,
      sizePx: topFit.sizePx,
      lineHeight: LINE.badgeSmall,
      font: topFont,
      align: 'center',
      width: textW,
      css: { ...cardTypeCss(topFont.style), color: look.color, ...look.small },
    })
  );
  push(
    cardText({
      role: 'badgeValue',
      x: textX,
      y,
      lines: valueFit.lines,
      sizePx: valueFit.sizePx,
      lineHeight: LINE.badgeValue,
      font: display,
      align: 'center',
      width: textW,
      css: { ...cardTypeCss(display.style), color: look.color, ...neonValue },
    })
  );
  if (subFit !== null) {
    push(
      cardText({
        role: 'badgeSub',
        x: textX,
        y,
        lines: subFit.lines,
        sizePx: subFit.sizePx,
        lineHeight: LINE.badgeSmall,
        font: subFont,
        align: 'center',
        width: textW,
        css: { ...cardTypeCss(subFont.style), color: look.color, ...look.small },
      })
    );
  }
  const plateH = y - LINK_PREVIEW_SIZES.badge.gap + look.padBottom + look.border;
  const top = input.centreY - plateH / 2;
  const plate: KitCardItem = {
    kind: 'shape',
    role: 'badge',
    box: { x: round2(input.x), y: round2(top), w: round2(plateW), h: round2(plateH) },
    css: look.plate,
  };
  const cx = input.x + plateW / 2;
  const items: KitCardItem[] = [plate, ...texts.map((item) => shiftCardItem(item, 0, top))];
  return items.map((item) => turnAbout(item, look.turn, cx, input.centreY));
}

/**
 * The layout of one link preview: the left column ("New single", the title, the artist; Noir an
 * accent line) right-aligned to the cover, the cover centred in the file, the
 * badge left-aligned right of the cover. All three are centred on the height of the content box.
 * Free: the watermark lockup under the badge (`linkPreviewWatermark`). Pure.
 */
export function linkPreviewLayout(input: KitCardLayoutInput<KitLinkPreviewCard>): KitCardLayout {
  const { card, themeId, colors } = input;
  const id = storyThemeIdOf(themeId);
  const factors = LINK_PREVIEW_FACTORS[id];
  const display: KitCardFont = { id: input.fonts.display, style: input.typeStyle.display };
  const text: KitCardFont = { id: input.fonts.text, style: input.typeStyle.text };
  const { column } = LINK_PREVIEW_SIZES;
  const centreY = LINK_PREVIEW_BOX.y + LINK_PREVIEW_BOX.h / 2;
  const leftRight = LINK_PREVIEW_BOX.x + column;
  const badgeX = LINK_PREVIEW_BOX.x + LINK_PREVIEW_BOX.w - column;

  const kickerFont: KitCardFont = {
    id: text.id,
    style: { ...text.style, weight: 800, tracking: Math.max(text.style.tracking, 0.12) },
  };
  const kickerFit = fitCardText(card.kicker, kickerFont, LINK_PREVIEW_SIZES.kicker * factors.text, column);
  const titleSize = LINK_PREVIEW_SIZES.title * factors.title * input.typeStyle.display.scale;
  const markerPad = id === 'handmade' ? 28 : 0;
  const titleFit = fitCardText(card.title, display, titleSize, column - markerPad, {
    maxLines: 2,
    floor: Math.min(1, LINK_PREVIEW_SIZES.titleFloorPx / titleSize),
  });
  const artistFont: KitCardFont = { id: text.id, style: { ...text.style, weight: 700 } };
  const artistFit = fitCardText(card.artist, artistFont, LINK_PREVIEW_SIZES.artist * factors.text, column, {
    maxLines: 2,
  });

  // Each item of the left column is built at x 0 and moved to end at the right edge of the column.
  const rightAlign = <T extends KitCardItem>(item: T): T => shiftCardItem(item, leftRight - item.box.w - item.box.x, 0);
  const parts: Array<(top: number) => KitCardItem> = [
    (top) =>
      rightAlign(
        cardText({
          role: 'kicker',
          x: 0,
          y: top,
          lines: kickerFit.lines,
          sizePx: kickerFit.sizePx,
          lineHeight: LINE.kicker,
          font: kickerFont,
          align: 'right',
          css: { ...cardTypeCss(kickerFont.style), color: colors.text },
        })
      ),
    (top) => {
      const item = cardText({
        role: 'title',
        x: 0,
        y: top,
        lines: titleFit.lines,
        sizePx: titleFit.sizePx,
        lineHeight: LINE.title,
        font: display,
        align: 'right',
        css: { ...cardTypeCss(display.style), ...titleCss(id, colors) },
        lineCss:
          id === 'handmade'
            ? {
                background: `linear-gradient(transparent 60%, ${storyAlpha(colors.accent, 0.45)} 60% 92%, transparent 92%)`,
                padding: '0 14px',
              }
            : undefined,
      });
      return rightAlign(markerPad === 0 ? item : { ...item, box: { ...item.box, w: round2(item.box.w + markerPad) } });
    },
    (top) =>
      rightAlign(
        cardText({
          role: 'artist',
          x: 0,
          y: top,
          lines: artistFit.lines,
          sizePx: artistFit.sizePx,
          lineHeight: LINE.artist,
          font: artistFont,
          align: 'right',
          css: { ...cardTypeCss(artistFont.style), color: colors.text },
        })
      ),
  ];
  if (id === 'dark') {
    parts.push((top) => ({
      kind: 'shape',
      role: 'line',
      box: {
        x: leftRight - LINK_PREVIEW_SIZES.noirLine.w,
        y: round2(top),
        w: LINK_PREVIEW_SIZES.noirLine.w,
        h: LINK_PREVIEW_SIZES.noirLine.h,
      },
      css: { background: colors.accent },
    }));
  }
  const left: KitCardItem[] = [];
  let bottom = 0;
  parts.forEach((part, index) => {
    const item = part(bottom);
    left.push(item);
    bottom = item.box.y + item.box.h + (index < parts.length - 1 ? LINK_PREVIEW_SIZES.leftGap : 0);
  });
  const leftTop = centreY - bottom / 2;

  const frame = cardCoverFrame(themeId, colors, COVER_SPEC);
  const coverW = coverWidth(id);
  const coverH = frame.heightOf(coverW);
  const coverX = LINK_PREVIEW_SIZE.width / 2 - coverW / 2;
  const badge = badgeItems({ card, id, colors, display, text, factors, x: badgeX, centreY });
  const items: KitCardItem[] = [
    {
      kind: 'cover',
      box: { x: round2(coverX), y: round2(centreY - coverH / 2), w: coverW, h: round2(coverH) },
      outer: frame.outer,
      picture: frame.picture,
      tape: frame.tape,
      record: frame.record,
    },
    ...left.map((item) => shiftCardItem(item, 0, leftTop)),
    ...badge,
  ];
  if (input.watermark === true) {
    items.push(linkPreviewWatermark(badge, badgeX));
  }
  // The columns end 40 px (`columnGap`) from the cover column (x 375 to 825) by construction.
  return {
    size: LINK_PREVIEW_SIZE,
    ground: linkPreviewGround(themeId, colors),
    deco: linkPreviewDeco(themeId, colors),
    items,
  };
}
